import { AuctionStatus } from '@/types/LiveAuctionState';
import { BidCanvasContainer } from './components/BidCanvasContainer/BidCanvasContainer';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit/bidLimit.actions';
import {
    fetchCatalogRegistrationsForUserByIdIfNeeded,
    submitAutoApproval,
} from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderIsSuspended, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getCatalogIsDone } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getIsRegistered } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemModelBuyNowStatus,
    getItemModelCatalogId,
    getItemModelCatalogStatus,
    getItemModelCatalogTitle,
    getItemModelIsSold,
    getItemModelIsTimedPlusAuction,
    getItemModelLotNumber,
    getItemModelSellerId,
    getItemModelSellerName,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSellerInInvisibleApprovalBeta } from '@/redux/modules/seller';
import { LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAddTimedPlusBidSuggestionsToBidCanvasExperiment } from '@/utils/amplitude/experiments/hooks/useAddTimedPlusBidSuggestionsToBidCanvasExperiment';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect } from 'react';
import BiddingBox from './components/BiddingBox/BiddingBox';
import ItemBiddingEstimate from './components/Estimate/ItemBiddingEstimate';
import ItemBidToast from './ItemBidToast';
import OpenForBidding from '@/components/ItemBidding/components/OpenForBidding/OpenForBidding';
import RegistrationMessage from '@/components/BidMessage/RegistrationMessage';
import styled from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import TimedPlusBiddingInfo from './components/TimedPlus/TimedPlusBiddingInfo';
import useIsWiderThan from '@/hooks/useIsWiderThan';

type Props = {
    itemId: number;
    onCountdownEnd: () => void;
};

const ItemBidding = ({ itemId, onCountdownEnd }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const bidderIsSuspended = useAppSelector(getBidderIsSuspended);

    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const catalogTitle = useAppSelector((state) => getItemModelCatalogTitle(state, itemId));
    const isTimedPlusAuction = useAppSelector((state) => getItemModelIsTimedPlusAuction(state, itemId));
    const isLoggedIn = useAppSelector((state) => isUserLoggedIn(state));
    const lotNumber = useAppSelector((state) => getItemModelLotNumber(state, itemId));
    const sellerId = useAppSelector((state) => getItemModelSellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemModelSellerName(state, itemId));
    const hasAuctionEnded = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isSold = useAppSelector((state) => getItemModelIsSold(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemModelBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemModelCatalogStatus(state, itemId));
    const buyNowAvailable = buyNowStatus === 1 && catalogStatus === AuctionStatus.Done;

    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const sellerInInvisibleApprovalBeta = useAppSelector((state) => getSellerInInvisibleApprovalBeta(state, sellerId));

    const mobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);

    useEffect(() => {
        if (sellerInInvisibleApprovalBeta && isLoggedIn) {
            (async () => {
                if (!registered && !bidderIsSuspended && !hasAuctionEnded && !isSold) {
                    await dispatch(submitAutoApproval({ catalogId, itemId }));
                }
                await Promise.all([
                    dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, itemId })),
                    dispatch(fetchCatalogRegistrationsForUserByIdIfNeeded({ catalogIds: [catalogId], force: true })),
                ]);
            })();
        }
    }, [
        sellerId,
        catalogId,
        itemId,
        dispatch,
        sellerInInvisibleApprovalBeta,
        isLoggedIn,
        registered,
        bidderIsSuspended,
        hasAuctionEnded,
        isSold,
    ]);

    const showSendSellerMessageModalDeclined = useCallback(() => {
        const messageDetails = {
            catalogId,
            message: `${formatMessage({
                id: 'message_registration_declined',
            })}`,
            title: catalogTitle,
        };

        const subject = `${formatMessage({
            id: 'registration_declined',
        })}: ${catalogTitle}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, catalogTitle, dispatch, formatMessage, sellerId, sellerName]);

    // Experiment to add timed plus bid suggestions to the bid canvas
    const isBidCanvasTimedPlusBidSuggestionsEnabled = useAddTimedPlusBidSuggestionsToBidCanvasExperiment();
    // the control version of the bid canvas does not support timed+ auctions, so we need to keep the conditional in here to use the legacy TimedPlus canvas from ItemBidding
    const isBidCanvasEnabled =
        isBidCanvasTimedPlusBidSuggestionsEnabled || !isTimedPlusAuction || (isTimedPlusAuction && buyNowAvailable);
    if (isBidCanvasEnabled) {
        return (
            <>
                <ItemBidToast
                    catalogId={catalogId}
                    itemId={itemId}
                />
                <BidCanvasContainer
                    catalogId={catalogId}
                    itemId={itemId}
                    onCountdownEnd={onCountdownEnd}
                />
            </>
        );
    }

    return (
        <div className="mt-sm">
            <ItemBidToast
                catalogId={catalogId}
                itemId={itemId}
            />
            {mobile && (
                <StyledLabelMini color="secondary">
                    <FormattedMessage
                        id="itemBidding.lotNumber"
                        values={{ lotNumber }}
                    />
                </StyledLabelMini>
            )}
            <ItemBiddingEstimate
                catalogId={catalogId}
                itemId={itemId}
                onCountdownEnd={onCountdownEnd}
            />
            <BiddingBox itemId={itemId}>
                {isTimedPlusAuction ? (
                    <TimedPlusBiddingInfo
                        catalogId={catalogId}
                        itemId={itemId}
                    />
                ) : (
                    <OpenForBidding itemId={itemId} />
                )}
            </BiddingBox>
            {(!isTimedPlusAuction || registered) && (
                <StyledRegistrationMessage
                    catalogId={catalogId}
                    itemId={itemId}
                    showSendSellerMessageModalDeclined={showSendSellerMessageModalDeclined}
                />
            )}
        </div>
    );
};

export default ItemBidding;

const StyledLabelMini = styled(LabelMini)`
    display: block;
`;

const StyledRegistrationMessage = styled(RegistrationMessage)`
    @media print {
        margin: 0;
    }
`;
